import React, { useEffect, useState, useCallback, useRef } from "react";
import { MainLayout } from "../MainLayout";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../../lib/supabase_client";
import { useSession } from "../../contexts/SessionContext";
import SourceFilter, { DEFAULT_SOURCE_FILTER, matchesSourceFilter, SourceFilterValue } from "./SourceFilter";
import { downloadXBRL } from "../../services/xbrlService";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../../styles/page-base.css';

dayjs.extend(relativeTime);

const DEFAULT_TIME_FROM = '00:00';
const DEFAULT_TIME_TO = '23:59';
const todayDateStr = () => dayjs().format('YYYY-MM-DD');

/** Build a local Date for YYYY-MM-DD at HH:mm. */
const atDateTime = (dateStr: string, hhmm: string, endOfMinute = false) => {
  const [h, m] = hhmm.split(':').map((v) => parseInt(v, 10));
  const d = dayjs(dateStr).toDate();
  d.setHours(h || 0, m || 0, endOfMinute ? 59 : 0, endOfMinute ? 999 : 0);
  return d;
};

// Processing steps mapping (same as AIlFiling.tsx)
const PROCESSING_STEPS: Record<string, number> = {
  'raw_upload': 5,
  'chunk_processor': 20,
  'gpt_analyzer': 25,
  'taxonomy_generator': 30,
  'form_generator': 35,
  'preprocess_data': 40,
  'mapping': 65,
  'write_form': 85,
  'combine_json': 100
};

// Accounting standards accepted by the reconversion state machine
const FILING_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'FS-MFRS', label: 'FS-MFRS — Malaysian Financial Reporting Standards' },
  { value: 'FS-MPERS', label: 'FS-MPERS — Private Entities Reporting Standard' },
  { value: 'FS-CLBG', label: 'FS-CLBG — Company Limited by Guarantee' },
  { value: 'FS-BNM', label: 'FS-BNM — Bank Negara Malaysia' },
];

interface FilingDocument {
  id: string;
  company_name: string;
  year_end: string;
  status: string;
  country_iso: string;
  created_at: string;
  updated_at: string;
  processing_status?: {
    status?: string;
    current_step?: string;
    last_updated?: string;
  };
  is_auditplus?: boolean;
  deleted_at?: string | null;
}

const toFilingDocument = (filing: any): FilingDocument => {
  let companyName = 'Analysing file...';
  let yearEnd = 'No Year End';

  if (filing.company_info) {
    const companyInfo = typeof filing.company_info === 'string'
      ? JSON.parse(filing.company_info)
      : filing.company_info;

    if (companyInfo && Object.keys(companyInfo).length > 0) {
      companyName = companyInfo.company_name || 'Unknown Company';
    }

    const yearEndRaw = companyInfo?.currentPeriod?.end || companyInfo?.reporting_period?.end || '';
    yearEnd = yearEndRaw
      ? `${yearEndRaw.substring(0, 4)}-${yearEndRaw.substring(4, 6)}-${yearEndRaw.substring(6, 8)}`
      : 'No Year End';
  }

  return {
    id: filing.id,
    company_name: companyName,
    year_end: yearEnd,
    status: filing.status,
    country_iso: (filing.country_iso || 'MY').toUpperCase(),
    created_at: filing.created_at,
    updated_at: filing.updated_at,
    processing_status: filing.processing_status,
    is_auditplus: Boolean(filing.is_auditplus),
    deleted_at: filing.deleted_at,
  };
};

export default function AdminOrganizationFilings() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { user } = useSession();
  const [searchTerm, setSearchTerm] = useState("");
  const [documents, setDocuments] = useState<FilingDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [downloadingFilingId, setDownloadingFilingId] = useState<string | null>(null);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [deletedDocuments, setDeletedDocuments] = useState<FilingDocument[]>([]);
  const [deletedCount, setDeletedCount] = useState<number | null>(null);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [recycleSearch, setRecycleSearch] = useState('');
  const [showDeleteFilingConfirm, setShowDeleteFilingConfirm] = useState<string | null>(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState<string | null>(null);
  const [deleteWithRefund, setDeleteWithRefund] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [retryingFilingId, setRetryingFilingId] = useState<string | null>(null);
  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);
  const [downloadingZipId, setDownloadingZipId] = useState<string | null>(null);
  const [downloadMenu, setDownloadMenu] = useState<{ doc: FilingDocument; top: number; right: number } | null>(null);
  const [showPdfErrorModal, setShowPdfErrorModal] = useState(false);
  const [standardChangeFilingId, setStandardChangeFilingId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [selectedSource, setSelectedSource] = useState<SourceFilterValue>(DEFAULT_SOURCE_FILTER);
  const [selectedFilingType, setSelectedFilingType] = useState<string>('');
  const [changingStandardId, setChangingStandardId] = useState<string | null>(null);
  // null appliedDate = all dates (org history). Apply a date to narrow like Today Filings.
  const [filterDate, setFilterDate] = useState(todayDateStr);
  const [appliedDate, setAppliedDate] = useState<string | null>(null);
  const [timeFrom, setTimeFrom] = useState(DEFAULT_TIME_FROM);
  const [timeTo, setTimeTo] = useState(DEFAULT_TIME_TO);
  const [appliedTimeFrom, setAppliedTimeFrom] = useState(DEFAULT_TIME_FROM);
  const [appliedTimeTo, setAppliedTimeTo] = useState(DEFAULT_TIME_TO);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const fetchOrganizationInfo = useCallback(async () => {
    if (!orgId) return;
    
    const { data, error } = await supabase
      .from('organizations')
      .select('name')
      .eq('id', orgId)
      .maybeSingle();

    if (!error && data) {
      setOrganizationName(data.name);
    }
  }, [orgId]);

  const getStatusColor = (status: string) => {
    const base: React.CSSProperties = {
      minWidth: 90,
      height: 24,
      padding: '0 8px',
      borderRadius: 9999,
      fontSize: 10,
      fontWeight: 500, // was 700
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  
    switch (status) {
      case 'validated':
        return { ...base, backgroundColor: 'rgba(88,232,203,0.22)', borderColor: 'rgba(88,232,203,0.55)', color: '#065f53' };
      case 'completed':
        return { ...base, backgroundColor: 'rgba(0,174,239,0.14)', borderColor: 'rgba(0,174,239,0.35)', color: 'var(--theme-600)' };
      case 'converted':
        return { ...base, backgroundColor: 'rgba(199,100,244,0.18)', borderColor: 'rgba(199,100,244,0.45)', color: '#5b1f7a' };
      case 'submitted':
        return { ...base, backgroundColor: 'rgba(0,174,239,0.14)', borderColor: 'rgba(0,174,239,0.35)', color: 'var(--theme-600)' };
      case 'processing':
      case 'archived':
      case 'failed':
        return { ...base, backgroundColor: 'rgba(244,100,100,0.20)', borderColor: 'rgba(244,100,100,0.55)', color: '#7a1f1f' };
      case 'draft':
      default:
        return { ...base, backgroundColor: 'var(--bg-white-dark)', borderColor: 'var(--bg-white-darker)', color: 'var(--dark)' };
    }
  };

  const fetchDocuments = useCallback(async () => {
    if (!orgId) return;

    try {
      setLoading(true);

      // Optional date window (same helper as Today Filings)
      let fromDate: Date | null = null;
      let toDate: Date | null = null;
      if (appliedDate) {
        fromDate = atDateTime(appliedDate, appliedTimeFrom, false);
        toDate = atDateTime(appliedDate, appliedTimeTo, true);
        if (fromDate.getTime() > toDate.getTime()) {
          alert('From time must be earlier than or equal to To time.');
          setLoading(false);
          return;
        }
      }

      // Fetch without embeds — nested selects break reliable .range() paging past 1000 rows
      const BATCH_SIZE = 1000;
      const allFilings: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        let query = supabase
          .from('filings')
          .select(`
            id,
            company_info,
            status,
            country_iso,
            created_at,
            updated_at,
            processing_status,
            is_auditplus,
            deleted_at
          `)
          .eq('org_id', orgId)
          .is('deleted_at', null);

        if (fromDate && toDate) {
          query = query
            .gte('created_at', fromDate.toISOString())
            .lte('created_at', toDate.toISOString());
        }

        const { data: batchFilings, error } = await query
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .range(offset, offset + BATCH_SIZE - 1);

        if (error) {
          console.error('Error fetching documents:', error);
          setDocuments([]);
          return;
        }

        if (batchFilings && batchFilings.length > 0) {
          allFilings.push(...batchFilings);
          offset += batchFilings.length;
          hasMore = batchFilings.length === BATCH_SIZE;
        } else {
          hasMore = false;
        }
      }

      console.log(`Fetched ${allFilings.length} filings for org ${orgId}`);

      setDocuments(allFilings.map(toFilingDocument));
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, appliedDate, appliedTimeFrom, appliedTimeTo]);

  const fetchDeletedCount = useCallback(async () => {
    if (!orgId) return;
    const { count, error } = await supabase
      .from('filings')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .not('deleted_at', 'is', null);

    if (!error) setDeletedCount(count ?? 0);
  }, [orgId]);

  const fetchDeletedFilings = useCallback(async () => {
    if (!orgId) return;

    try {
      setDeletedLoading(true);
      const BATCH_SIZE = 1000;
      const allFilings: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data: batchFilings, error } = await supabase
          .from('filings')
          .select(`
            id,
            company_info,
            status,
            country_iso,
            created_at,
            updated_at,
            processing_status,
            is_auditplus,
            deleted_at
          `)
          .eq('org_id', orgId)
          .not('deleted_at', 'is', null)
          .order('deleted_at', { ascending: false })
          .order('id', { ascending: false })
          .range(offset, offset + BATCH_SIZE - 1);

        if (error) {
          console.error('Error fetching deleted filings:', error);
          setDeletedDocuments([]);
          return;
        }

        if (batchFilings && batchFilings.length > 0) {
          allFilings.push(...batchFilings);
          offset += batchFilings.length;
          hasMore = batchFilings.length === BATCH_SIZE;
        } else {
          hasMore = false;
        }
      }

      setDeletedDocuments(allFilings.map(toFilingDocument));
      setDeletedCount(allFilings.length);
    } catch (error) {
      console.error('Error fetching deleted filings:', error);
      setDeletedDocuments([]);
    } finally {
      setDeletedLoading(false);
    }
  }, [orgId]);

  const applyDateTimeRange = () => {
    if (!filterDate) {
      alert('Please select a date.');
      return;
    }
    if (!timeFrom || !timeTo) {
      alert('Please select both From and To time.');
      return;
    }
    if (atDateTime(filterDate, timeFrom).getTime() > atDateTime(filterDate, timeTo, true).getTime()) {
      alert('From time must be earlier than or equal to To time.');
      return;
    }
    setAppliedDate(filterDate);
    setAppliedTimeFrom(timeFrom);
    setAppliedTimeTo(timeTo);
    setShowFilterPanel(false);
  };

  const dateFilterActive = appliedDate != null;

  // Close date/time popover on outside click / Escape
  useEffect(() => {
    if (!showFilterPanel) return;
    const onPointerDown = (e: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target as Node)) {
        setShowFilterPanel(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowFilterPanel(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showFilterPanel]);

  useEffect(() => {
    if (!downloadMenu) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.download-menu-dropdown') && !target.closest('.download-menu-button')) {
        setDownloadMenu(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDownloadMenu(null);
    };
    const onScroll = () => setDownloadMenu(null);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [downloadMenu]);

  const handleDeleteFiling = async (filingId: string, withRefund: boolean = false, reason: string = '') => {
    try {
      setIsDeleting(true);

      if (withRefund) {
        // Use the refund RPC function
        const { data, error } = await supabase.rpc('process_filing_refund', {
          p_filing_id: filingId,
          p_reason: reason,
          p_refunded_by: user?.id
        });

        if (error) {
          console.error('Error processing refund:', error);
          alert('Failed to process refund. Please try again.');
          return;
        }

        // Refresh wallet balances
        await supabase.rpc('refresh_wallet_balances');

        if (data?.refunded) {
          alert(`Filing deleted and ${data.refund_amount} credit(s) refunded successfully.`);
        } else {
          alert('Filing deleted. No credit transaction found to refund.');
        }
      } else {
        // Simple soft delete without refund
        const { error } = await supabase
          .from('filings')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', filingId);

        if (error) {
          console.error('Error deleting filing:', error);
          alert('Failed to delete filing. Please try again.');
          return;
        }
      }

      fetchDocuments();
      fetchDeletedCount();
      console.log('Filing deleted successfully');
    } catch (error) {
      console.error('Error deleting filing:', error);
      alert('Failed to delete filing. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteWithRefund(false);
      setRefundReason('');
    }
  };

  const handleRestoreFiling = async (filingId: string) => {
    try {
      setIsRestoring(true);

      const { data, error } = await supabase
        .from('filings')
        .update({ deleted_at: null })
        .eq('id', filingId)
        .select('id');

      if (error || !data?.length) {
        console.error('Error restoring filing:', error);
        alert('Failed to restore filing. Please try again.');
        return;
      }

      setDeletedDocuments((prev) => prev.filter((doc) => doc.id !== filingId));
      setDeletedCount((prev) => (prev == null ? prev : Math.max(0, prev - 1)));
      fetchDocuments();
    } catch (error) {
      console.error('Error restoring filing:', error);
      alert('Failed to restore filing. Please try again.');
    } finally {
      setIsRestoring(false);
      setShowRestoreConfirm(null);
    }
  };

  const handleDownloadFiling = async (doc: FilingDocument) => {
    try {
      setDownloadingFilingId(doc.id);
      console.log(`Generating XBRL for filing: ${doc.id} (${doc.country_iso})`);
      await downloadXBRL(doc.id, doc.country_iso === 'SG' ? 'SG' : 'MY');
      console.log('XBRL file downloaded successfully!');
    } catch (error) {
      console.error('Error downloading filing:', error);
      alert('Failed to download filing. Please try again.');
    } finally {
      setDownloadingFilingId(null);
    }
  };

  useEffect(() => {
    if (orgId) {
      fetchOrganizationInfo();
      fetchDocuments();
      fetchDeletedCount();
    }
  }, [orgId, fetchOrganizationInfo, fetchDocuments, fetchDeletedCount]);

  useEffect(() => {
    if (showRecycleBin) fetchDeletedFilings();
  }, [showRecycleBin, fetchDeletedFilings]);

  // Track if there are processing filings (for realtime subscription)
  const hasProcessingFilings = documents.some(doc => doc.status === 'processing');

  // Supabase Realtime - Live status updates for processing filings
  // IMPORTANT: Only depend on orgId and hasProcessingFilings (boolean), NOT documents array
  // Otherwise every realtime update causes re-subscribe loop
  useEffect(() => {
    if (!orgId) return;
    
    // Only subscribe if there are processing filings
    if (!hasProcessingFilings) {
      console.log('⏸️ No processing filings - skipping Realtime subscription');
      return;
    }
    
    console.log('🔴 Setting up Realtime subscription for organization filings...');
    
    // Subscribe to filings table changes - filtered by org_id
    const channel = supabase
      .channel(`org-filings-realtime-${orgId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'filings',
          filter: `org_id=eq.${orgId}`
        },
        (payload) => {
          console.log('🔴 Realtime update received:', payload.new);
          
          const updatedFiling = payload.new as any;
          
          // Update the filing in state
          setDocuments(prev => {
            const index = prev.findIndex(d => d.id === updatedFiling.id);
            if (index === -1) return prev; // Filing not in list

            if (updatedFiling.deleted_at) {
              return prev.filter(d => d.id !== updatedFiling.id);
            }
            
            // Parse company_info
            const companyInfo = typeof updatedFiling.company_info === 'string'
              ? JSON.parse(updatedFiling.company_info)
              : updatedFiling.company_info;
            
            // Extract company name and year end
            let companyName = 'Analysing file...';
            if (companyInfo && Object.keys(companyInfo).length > 0) {
              companyName = companyInfo.company_name || 'Unknown Company';
            }
            
            const yearEndRaw = companyInfo?.currentPeriod?.end || companyInfo?.reporting_period?.end || '';
            const yearEnd = yearEndRaw
              ? `${yearEndRaw.substring(0, 4)}-${yearEndRaw.substring(4, 6)}-${yearEndRaw.substring(6, 8)}`
              : 'No Year End';
            
            const existingDoc = prev[index];

            // Create updated filing object — keep is_auditplus so Retry / Change Standard stay disabled
            const updated: FilingDocument = {
              ...existingDoc,
              id: updatedFiling.id,
              company_name: companyName,
              year_end: yearEnd,
              status: updatedFiling.status,
              country_iso: updatedFiling.country_iso ? (updatedFiling.country_iso as string).toUpperCase() : existingDoc.country_iso,
              created_at: updatedFiling.created_at || existingDoc.created_at,
              updated_at: updatedFiling.updated_at,
              processing_status: updatedFiling.processing_status,
              is_auditplus: updatedFiling.is_auditplus != null
                ? Boolean(updatedFiling.is_auditplus)
                : Boolean(existingDoc.is_auditplus),
            };
            
            // Replace the filing in array
            const newDocs = [...prev];
            newDocs[index] = updated;
            return newDocs;
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime subscription active for organization filings');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Realtime subscription error');
        } else if (status === 'TIMED_OUT') {
          console.warn('⚠️ Realtime subscription timed out - will retry');
        }
      });
    
    // Cleanup on unmount or when hasProcessingFilings changes
    return () => {
      console.log('🔴 Cleaning up Realtime subscription');
      channel.unsubscribe();
    };
  }, [orgId, hasProcessingFilings]);

  const filteredDeleted = deletedDocuments.filter((doc) => {
    const term = recycleSearch.trim().toLowerCase();
    if (!term) return true;
    return doc.company_name.toLowerCase().includes(term) || doc.year_end.toLowerCase().includes(term);
  });

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.year_end.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'ALL' || doc.status === selectedStatus;
    const matchesSource = matchesSourceFilter(doc.is_auditplus, selectedSource);
    return matchesSearch && matchesStatus && matchesSource;
  });

  const totalPages = Math.ceil(filteredDocuments.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentDocuments = filteredDocuments.slice(startIndex, endIndex);

  const handleDownloadPdf = async (doc: FilingDocument) => {
    try {
      setDownloadingPdfId(doc.id);
      
      // Call edge function to get PDF URL
      const { data, error } = await supabase.functions.invoke('get-raw-upload-pdf', {
        body: { org_id: orgId, filing_id: doc.id }
      });
  
      // Check for error first
      if (error) {
        console.error('Supabase function error:', error);
        setShowPdfErrorModal(true);
        return;
      }
  
      // Check if data exists and has an error field (404 response)
      if (!data || data.error || !data.pdf_url) {
        console.error('PDF not found in response:', data);
        setShowPdfErrorModal(true);
        return;
      }
  
      // Validate that pdf_url is actually a valid URL before opening
      if (data.pdf_url && typeof data.pdf_url === 'string' && data.pdf_url.startsWith('http')) {
        // Open PDF in new tab
        window.open(data.pdf_url, '_blank');
      } else {
        console.error('Invalid PDF URL:', data.pdf_url);
        setShowPdfErrorModal(true);
      }
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      // Show modal for any error
      setShowPdfErrorModal(true);
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const handleDownloadPipelineZip = async (doc: FilingDocument) => {
    try {
      setDownloadingZipId(doc.id);
      const { data, error } = await supabase.functions.invoke('get-filing-pipeline-zip', {
        body: { org_id: orgId, filing_id: doc.id, company_name: doc.company_name }
      });

      if (error) {
        console.error('Pipeline zip function error:', error);
        alert(error.message || 'Failed to download pipeline zip.');
        return;
      }
      if (!data || data.error || !data.zip_url) {
        console.error('Pipeline zip not found:', data);
        alert(data?.message || data?.error || 'No converted pipeline files found for this filing.');
        return;
      }
      if (typeof data.zip_url === 'string' && data.zip_url.startsWith('http')) {
        const filename = data.filename || `filing_${doc.id}.zip`;
        try {
          const res = await fetch(data.zip_url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(objectUrl);
        } catch {
          const a = document.createElement('a');
          a.href = data.zip_url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      } else {
        alert('Invalid zip download URL.');
      }
    } catch (error: any) {
      console.error('Error downloading pipeline zip:', error);
      alert(error?.message || 'Failed to download pipeline zip.');
    } finally {
      setDownloadingZipId(null);
    }
  };

  const handleRetryFiling = async (filingId: string) => {
    try {
      setRetryingFilingId(filingId);
      
      // Get filing details to get org_id
      const { data: filingData, error: filingError } = await supabase
        .from('filings')
        .select('org_id, is_auditplus')
        .eq('id', filingId)
        .single();

      if (filingError || !filingData) {
        alert('Failed to find filing. Please try again.');
        return;
      }

      if (filingData.is_auditplus) {
        alert('Retry is disabled for Audit+ filings to avoid OCR reconversion.');
        return;
      }

      // Update filing status to processing and reset processing_status
      const { error: updateError } = await supabase
        .from('filings')
        .update({
          status: 'processing',
          processing_status: {
            status: 'completed',
            current_step: 'raw_upload',
            last_updated: new Date().toISOString()
          }
        })
        .eq('id', filingId);

      if (updateError) {
        console.error('Error updating filing status:', updateError);
        alert('Failed to update filing status. Please try again.');
        return;
      }

      // Get session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        alert('Please log in first');
        return;
      }

      // Call upload-filing-pdf with retry flag (same pattern as AIlFiling.tsx)
      const formData = new FormData();
      formData.append('org_id', filingData.org_id);
      formData.append('filing_id', filingId);
      formData.append('retry', 'true'); // Flag to indicate retry mode

      const { data: uploadData, error: uploadError } = await supabase.functions.invoke(
        'upload-filing-pdf',
        {
          body: formData,
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (uploadError) {
        console.error('Error triggering retry:', uploadError);
        alert('Failed to trigger retry. Please try again.');
        return;
      }

      console.log('Filing retry initiated successfully:', uploadData);
      
      // Refresh the documents list
      fetchDocuments();
    } catch (error) {
      console.error('Error retrying filing:', error);
      alert('Failed to retry filing. Please try again.');
    } finally {
      setRetryingFilingId(null);
    }
  };

  const handleChangeStandard = async (filingId: string, filingType: string) => {
    if (!orgId || !filingType) return;
    const targetDoc = documents.find((d) => d.id === filingId);
    if (targetDoc?.is_auditplus) {
      alert('Changing accounting standard is disabled for Audit+ filings to avoid OCR reconversion.');
      return;
    }
    try {
      setChangingStandardId(filingId);

      // Get session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Please log in first');
        return;
      }

      const { data, error } = await supabase.functions.invoke('trigger-standard-change', {
        body: {
          filing_id: filingId,
          org_id: orgId,
          filing_type: filingType,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error || (data && data.error)) {
        console.error('Error triggering standard change:', error || data.error);
        alert(`Failed to trigger reconversion: ${(data && data.error) || error?.message || 'Unknown error'}`);
        return;
      }

      console.log('Standard change initiated successfully:', data);
      setStandardChangeFilingId(null);
      setSelectedFilingType('');
      fetchDocuments();
    } catch (error) {
      console.error('Error changing standard:', error);
      alert('Failed to trigger reconversion. Please try again.');
    } finally {
      setChangingStandardId(null);
    }
  };

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="page-header">
            <div className="page-header-left">
                <div className="text-sm text-gray-500 mb-1">Admin Only</div>
                <h1 className="page-title">Filings - {organizationName || 'Loading...'}</h1>
                </div>
                <div className="page-header-right">
                <button
                onClick={() => navigate('/admin/customers')}
                className="f-btn f-btn-primary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Customers</span>
              </button>
            </div>
          </div>

          {/* Search + Status + Date/time */}
          <div className="page-card">
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search filings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <SourceFilter
                  value={selectedSource}
                  onChange={(next) => { setSelectedSource(next); setCurrentPage(1); }}
                />
                <select
                  value={selectedStatus}
                  onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  aria-label="Status"
                >
                  <option value="ALL">All statuses</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="converted">Converted</option>
                  <option value="validated">Validated</option>
                  <option value="submitted">Submitted</option>
                  <option value="draft">Draft</option>
                  <option value="failed">Failed</option>
                  <option value="archived">Archived</option>
                </select>

                <button
                  type="button"
                  onClick={() => {
                    setRecycleSearch('');
                    setShowRecycleBin(true);
                  }}
                  className="f-btn f-btn-outline"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
                  </svg>
                  <span>
                    {deletedCount != null ? `Recycle bin (${deletedCount})` : 'Recycle bin'}
                  </span>
                </button>

                <div className="relative" ref={filterPanelRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setFilterDate(appliedDate || todayDateStr());
                      setTimeFrom(appliedTimeFrom);
                      setTimeTo(appliedTimeTo);
                      setShowFilterPanel((v) => !v);
                    }}
                    className={`f-btn ${dateFilterActive ? 'f-btn-primary' : 'f-btn-outline'}`}
                    aria-expanded={showFilterPanel}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>
                      {dateFilterActive
                        ? `${dayjs(appliedDate).format('DD MMM')} ${appliedTimeFrom}–${appliedTimeTo}`
                        : 'Date / time'}
                    </span>
                  </button>

                  {showFilterPanel && (
                    <div className="absolute right-0 top-full mt-2 z-30 w-72 rounded-lg border border-gray-200 bg-white shadow-lg p-4 space-y-3">
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Date
                        </div>
                        <input
                          type="date"
                          value={filterDate}
                          onChange={(e) => setFilterDate(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Time range
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={timeFrom}
                            onChange={(e) => setTimeFrom(e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <span className="text-sm text-gray-400">to</span>
                          <input
                            type="time"
                            value={timeTo}
                            onChange={(e) => setTimeTo(e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setFilterDate(todayDateStr());
                            setAppliedDate(null);
                            setTimeFrom(DEFAULT_TIME_FROM);
                            setTimeTo(DEFAULT_TIME_TO);
                            setAppliedTimeFrom(DEFAULT_TIME_FROM);
                            setAppliedTimeTo(DEFAULT_TIME_TO);
                            setShowFilterPanel(false);
                          }}
                          className="f-btn f-btn-soft f-btn-xs"
                        >
                          All dates
                        </button>
                        <button
                          type="button"
                          onClick={applyDateTimeRange}
                          className="f-btn f-btn-primary f-btn-xs"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="page-table">
              <thead className="page-table-thead">
                  <tr className="page-table-tr">
                    <th className="page-table-th">Company Name</th>
                    <th className="page-table-th">Year End</th>
                    <th className="page-table-th">Status</th>
                    <th className="page-table-th">Created</th>
                    <th className="page-table-th">Updated</th>
                    <th className="page-table-th" style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
              </thead>
                <tbody className="page-table-tbody">
                  {loading ? (
                    <tr className="page-table-tr">
                      <td colSpan={6} className="page-table-td text-center py-4 text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : currentDocuments.length === 0 ? (
                    <tr className="page-table-tr">
                      <td colSpan={6} className="page-table-td text-center py-4 text-gray-500">
                        {dateFilterActive
                          ? 'No filings found for this date / time range.'
                          : 'No filings found.'}
                      </td>
                    </tr>
                  ) : (
                    currentDocuments.map((doc) => (
                      <tr key={doc.id} className="page-table-tr">
                        <td className="page-table-td">{doc.company_name}</td>
                        <td className="page-table-td">{doc.year_end}</td>
                        <td className="page-table-td">
                          {doc.status === 'processing' ? (
                            // Processing progress bar (same as AIlFiling.tsx)
                            <div 
                              style={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 90,
                                height: 24,
                                padding: '0 8px',
                                borderRadius: 9999,
                                fontSize: 10,
                                fontWeight: 500,
                                border: '1px solid #0083B3',
                                color: '#0083B3',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                                overflow: 'hidden' // 🔒 Clips fill to respect rounded corners
                              }}
                              title={`Processing: ${doc.processing_status?.current_step || 'Starting...'}`}
                            >
                              {/* Progress bar fill */}
                              <div 
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  height: '100%',
                                  backgroundColor: '#0083B3',
                                  opacity: 0.15,
                                  width: `${PROCESSING_STEPS[doc.processing_status?.current_step || 'raw_upload'] || 0}%`,
                                  transition: 'width 0.5s ease'
                                }}
                              />
                              {/* Percentage text */}
                              <span style={{ position: 'relative', zIndex: 1 }}>
                                {PROCESSING_STEPS[doc.processing_status?.current_step || 'raw_upload'] || 0}%
                              </span>
                            </div>
                          ) : (
                            // Regular status badge
                            <span style={getStatusColor(doc.status)}>
                              {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                            </span>
                          )}
                        </td>
                        <td className="page-table-td">
                          {dayjs(doc.created_at).fromNow()}
                        </td>
                        <td className="page-table-td">
                          {dayjs(doc.updated_at).fromNow()}
                        </td>
                        <td className="page-table-td" style={{ textAlign: 'center' }}>
                          <div className="flex justify-center gap-2">
                            <button 
                              className="f-btn f-btn-soft f-btn-xs"
                              onClick={() => handleDownloadPdf(doc)}
                              disabled={downloadingPdfId === doc.id}
                              title="Download PDF"
                            >
                              {downloadingPdfId === doc.id ? 'Loading...' : 'PDF'}
                            </button>
                            <Link 
                              to={`/all-filings/${orgId}/view/${doc.id}`}
                              className="f-btn f-btn-soft f-btn-xs"
                              title="View filing"
                            >
                              View
                            </Link>
                            <Link
                              to={`/all-filings/${orgId}/edit/${doc.id}`}
                              className="f-btn f-btn-soft f-btn-xs"
                              title="Edit filing"
                            >
                              Edit
                            </Link>
                            <button 
                              className="f-btn f-btn-soft f-btn-xs download-menu-button"
                              onClick={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setDownloadMenu((prev) =>
                                  prev?.doc.id === doc.id
                                    ? null
                                    : { doc, top: rect.bottom + 4, right: window.innerWidth - rect.right }
                                );
                              }}
                              disabled={downloadingFilingId === doc.id || downloadingZipId === doc.id}
                              title="Download XBRL or source zip"
                            >
                              {downloadingFilingId === doc.id
                                ? 'Generating...'
                                : downloadingZipId === doc.id
                                  ? 'Zipping...'
                                  : 'Download'}
                            </button>
                            <button
                              onClick={() => handleRetryFiling(doc.id)}
                              className="f-btn f-btn-soft f-btn-xs"
                              disabled={Boolean(doc.is_auditplus) || retryingFilingId === doc.id}
                              title={doc.is_auditplus ? 'Disabled for Audit+ filings' : doc.status === 'processing' ? 'Already processing' : 'Retry processing'}
                            >
                              {retryingFilingId === doc.id ? 'Retrying...' : 'Retry'}
                            </button>
                            <button
                              onClick={() => {
                                if (doc.is_auditplus) return;
                                setStandardChangeFilingId(doc.id);
                                setSelectedFilingType('');
                              }}
                              className="f-btn f-btn-soft f-btn-xs"
                              disabled={Boolean(doc.is_auditplus) || changingStandardId === doc.id}
                              title={doc.is_auditplus ? 'Disabled for Audit+ filings' : 'Reconvert with a different accounting standard'}
                              aria-label="Change accounting standard"
                            >
                              {changingStandardId === doc.id ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              )}
                            </button>
                            <button 
                              className="f-btn f-btn-danger f-btn-xs"
                              onClick={() => {
                                setShowDeleteFilingConfirm(doc.id);
                              }}
                              title="Delete filing"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && filteredDocuments.length > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
                <div className="flex items-center space-x-2">
                  <span>Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <span>
                  {startIndex + 1}-{Math.min(endIndex, filteredDocuments.length)} of {filteredDocuments.length}
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &lt;
                  </button>
                  <button className="f-btn f-btn-primary f-btn-xs">
                    {currentPage}
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Delete Filing Confirmation Modal */}
      {showDeleteFilingConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Filing</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this filing?
            </p>

            {/* Refund Option */}
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteWithRefund}
                  onChange={(e) => setDeleteWithRefund(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Delete and Refund Credit</span>
              </label>
            </div>

            {/* Reason Input - shown when refund is selected */}
            {deleteWithRefund && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Refund <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Enter reason for deletion and refund..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>
            )}

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteFilingConfirm(null);
                  setDeleteWithRefund(false);
                  setRefundReason('');
                }}
                className="f-btn f-btn-soft"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteWithRefund && !refundReason.trim()) {
                    alert('Please enter a reason for the refund.');
                    return;
                  }
                  handleDeleteFiling(showDeleteFilingConfirm, deleteWithRefund, refundReason);
                  setShowDeleteFilingConfirm(null);
                }}
                className="f-btn f-btn-danger"
                disabled={isDeleting}
              >
                {isDeleting ? 'Processing...' : deleteWithRefund ? 'Delete & Refund' : 'Delete Filing'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showRecycleBin && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9990] p-4"
          onClick={() => {
            if (!isRestoring) setShowRecycleBin(false);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Recycle bin</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Deleted filings stay here. Restore one to put it back on this page, then use Delete and Refund Credit if a refund is needed.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowRecycleBin(false)}
                className="f-btn f-btn-soft"
                disabled={isRestoring}
              >
                Close
              </button>
            </div>

            <input
              type="text"
              placeholder="Search deleted filings..."
              value={recycleSearch}
              onChange={(e) => setRecycleSearch(e.target.value)}
              className="mb-4 block w-full text-sm px-3 py-2 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />

            <div className="overflow-auto border border-gray-200 rounded-lg">
              <table className="page-table">
                <thead className="page-table-thead">
                  <tr className="page-table-tr">
                    <th className="page-table-th">Company Name</th>
                    <th className="page-table-th">Year End</th>
                    <th className="page-table-th">Status</th>
                    <th className="page-table-th">Deleted</th>
                    <th className="page-table-th" style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="page-table-tbody">
                  {deletedLoading ? (
                    <tr className="page-table-tr">
                      <td colSpan={5} className="page-table-td text-center py-4 text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredDeleted.length === 0 ? (
                    <tr className="page-table-tr">
                      <td colSpan={5} className="page-table-td text-center py-4 text-gray-500">
                        No deleted filings.
                      </td>
                    </tr>
                  ) : (
                    filteredDeleted.map((doc) => (
                      <tr key={doc.id} className="page-table-tr">
                        <td className="page-table-td">{doc.company_name}</td>
                        <td className="page-table-td">{doc.year_end}</td>
                        <td className="page-table-td">
                          <span style={getStatusColor(doc.status)}>
                            {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                          </span>
                        </td>
                        <td className="page-table-td">
                          {doc.deleted_at ? dayjs(doc.deleted_at).fromNow() : '—'}
                        </td>
                        <td className="page-table-td" style={{ textAlign: 'center' }}>
                          <div className="flex justify-center gap-2">
                            <button
                              className="f-btn f-btn-soft f-btn-xs"
                              onClick={() => handleDownloadPdf(doc)}
                              disabled={downloadingPdfId === doc.id}
                              title="Download PDF"
                            >
                              {downloadingPdfId === doc.id ? 'Loading...' : 'PDF'}
                            </button>
                            <button
                              className="f-btn f-btn-primary f-btn-xs"
                              onClick={() => setShowRestoreConfirm(doc.id)}
                              disabled={isRestoring}
                              title="Restore filing"
                            >
                              Restore
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Restore Filing</h3>
            <p className="text-gray-600 mb-6">
              This puts the filing back on the filings list. From there, use Delete and check Refund Credit if a refund is needed.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowRestoreConfirm(null)}
                className="f-btn f-btn-soft"
                disabled={isRestoring}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRestoreFiling(showRestoreConfirm)}
                className="f-btn f-btn-primary"
                disabled={isRestoring}
              >
                {isRestoring ? 'Restoring...' : 'Restore Filing'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Change Accounting Standard Modal */}
      {standardChangeFilingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Change Accounting Standard</h3>
            <p className="text-gray-600 text-sm mb-4">
              Select the accounting standard to reconvert this filing with. This triggers a fresh conversion
              pipeline and will overwrite the current converted output.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accounting Standard <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedFilingType}
                onChange={(e) => setSelectedFilingType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a standard...</option>
                {FILING_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setStandardChangeFilingId(null);
                  setSelectedFilingType('');
                }}
                className="f-btn f-btn-soft"
                disabled={changingStandardId === standardChangeFilingId}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!selectedFilingType) {
                    alert('Please select an accounting standard.');
                    return;
                  }
                  handleChangeStandard(standardChangeFilingId, selectedFilingType);
                }}
                className="f-btn f-btn-primary"
                disabled={!selectedFilingType || changingStandardId === standardChangeFilingId}
              >
                {changingStandardId === standardChangeFilingId ? 'Converting...' : 'Start Reconversion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {downloadMenu && (
        <div
          className="download-menu-dropdown fixed z-[9999] min-w-[148px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          style={{ top: downloadMenu.top, right: downloadMenu.right }}
        >
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-200 hover:text-gray-900"
            onClick={() => {
              const doc = downloadMenu.doc;
              setDownloadMenu(null);
              handleDownloadFiling(doc);
            }}
          >
            XBRL file
          </button>
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-200 hover:text-gray-900"
            onClick={() => {
              const doc = downloadMenu.doc;
              setDownloadMenu(null);
              handleDownloadPipelineZip(doc);
            }}
          >
            Source zip
          </button>
        </div>
      )}

      {/* PDF Error Modal */}
      {showPdfErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">PDF Not Available</h3>
            <p className="text-gray-600 mb-6">
              The PDF file for this filing is not available. It may not have been uploaded yet or may have been removed.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowPdfErrorModal(false)}
                className="f-btn f-btn-primary"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}